import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { LEGAL_SEED } from "@/lib/legalSeed";

const DOCS = [
  { key: "pro_terms", label: "Pro Terms & Conditions" },
  { key: "pro_privacy", label: "Pro Privacy Policy" },
];
const FIELDS = ["title", "version", "last_updated", "effective_from", "content"];
const pick = (d) => FIELDS.reduce((o, k) => ({ ...o, [k]: d?.[k] ?? "" }), {});

export default function LegalDocuments() {
  const { user } = useAuth();
  const [active, setActive] = useState("pro_terms");
  const [remote, setRemote] = useState({});
  const [form, setForm] = useState({ pro_terms: pick(LEGAL_SEED.pro_terms), pro_privacy: pick(LEGAL_SEED.pro_privacy) });
  const [busy, setBusy] = useState(false);
  const loaded = useRef({});

  useEffect(() => {
    const unsubs = DOCS.map((d) => adapter.subscribeDoc("legal_documents", d.key, (doc) => {
      setRemote((r) => ({ ...r, [d.key]: doc || null }));
      if (!loaded.current[d.key]) {
        loaded.current[d.key] = true;
        setForm((f) => ({ ...f, [d.key]: pick(doc || LEGAL_SEED[d.key]) }));
      }
    }));
    return () => unsubs.forEach((u) => u && u());
  }, []);

  const cur = form[active] || pick(LEGAL_SEED[active]);
  const setField = (k, v) => setForm((f) => ({ ...f, [active]: { ...f[active], [k]: v } }));

  const save = async () => {
    if (!cur.title?.trim() || !cur.content?.trim()) return toast.error("Title and content are required.");
    setBusy(true);
    try {
      await adapter.set("legal_documents", active, {
        title: cur.title, version: cur.version, last_updated: cur.last_updated,
        effective_from: cur.effective_from, content: cur.content,
        updated_at: new Date().toISOString(), updated_by: user?.email || "ops",
      });
      toast.success("Saved — Brugo Pro will show the updated document.");
    } catch (e) {
      toast.error(e?.code === "permission-denied" ? "Publish the legal_documents rule first (see chat)" : (e.message || "Save failed"));
    } finally { setBusy(false); }
  };

  const resetToAttached = () => {
    setForm((f) => ({ ...f, [active]: pick(LEGAL_SEED[active]) }));
    toast.message("Reverted to the attached v1.3 default (not yet saved).");
  };

  const r = remote[active];

  return (
    <Card className="p-6" data-testid="legal-docs-card">
      <div className="flex items-center gap-2 mb-1">
        <Icons.Scale className="h-5 w-5 text-primary" />
        <h3 className="font-heading font-semibold text-foreground">Legal Documents</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Edit the Brugo Pro Terms & Conditions and Privacy Policy. Saved content is read live by the Brugo Pro app from <span className="font-mono text-xs">legal_documents/{active}</span>. Only Command Centre staff can edit; pros can only read.</p>

      <div className="inline-flex rounded-lg border border-border p-1 mb-4">
        {DOCS.map((d) => (
          <button key={d.key} onClick={() => setActive(d.key)} data-testid={`legal-tab-${d.key}`}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${active === d.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {d.label}
          </button>
        ))}
      </div>

      <div className={`mb-4 rounded-lg border p-2.5 text-xs ${r ? "border-emerald-200 bg-emerald-50/50 text-emerald-700" : "border-amber-200 bg-amber-50/50 text-amber-700"}`} data-testid="legal-status">
        {r
          ? <>Saved in Firestore · v{r.version || "—"} · last updated {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}{r.updated_by ? ` · by ${r.updated_by}` : ""}</>
          : <>Not saved yet — showing the attached v1.3 default. Publish the <span className="font-mono">legal_documents</span> rule, then click Save to publish it to Brugo Pro.</>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div className="space-y-1.5 sm:col-span-2"><Label>Title</Label><Input data-testid="legal-title" value={cur.title} onChange={(e) => setField("title", e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Version</Label><Input data-testid="legal-version" value={cur.version} onChange={(e) => setField("version", e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Last updated</Label><Input data-testid="legal-lastupdated" value={cur.last_updated} onChange={(e) => setField("last_updated", e.target.value)} placeholder="e.g. 2 October 2026" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Effective from</Label><Input data-testid="legal-effective" value={cur.effective_from} onChange={(e) => setField("effective_from", e.target.value)} placeholder="e.g. 2 October 2026" /></div>
      </div>

      <div className="space-y-1.5 mb-4">
        <Label>Content</Label>
        <textarea data-testid="legal-content" value={cur.content} onChange={(e) => setField("content", e.target.value)}
          className="w-full min-h-[360px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring" />
        <p className="text-[11px] text-muted-foreground">{(cur.content || "").length.toLocaleString()} characters</p>
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="legal-save"><Icons.Save className="h-4 w-4" /> Save</Button>
        <Button variant="outline" onClick={resetToAttached} disabled={busy} className="gap-1.5" data-testid="legal-reset"><Icons.RotateCcw className="h-4 w-4" /> Revert to attached v1.3</Button>
      </div>
    </Card>
  );
}
